import React, { useState, useEffect } from "react";
import {
    ShieldCheck,
    FileText,
    Upload,
    CheckCircle2,
    Clock,
    AlertCircle,
    ChevronDown,
    ChevronUp,
    Plus,
    X,
    Info,
    History,
    TrendingUp,
    MessageSquare,
    MoreHorizontal,
    ExternalLink
} from "lucide-react";
import { db } from "../firebase";
import { doc, onSnapshot } from "firebase/firestore";
import ChatBox from "../components/msg/Chatbox";

// Mock data for the 5-step journey
const STEPS = [
    {
        id: "tradeCompliance",
        title: "Trade Compliance",
        description: "Customs and trade law compliance history.",
        status: "Approved",
        progress: 100,
        tasks: [
            { id: "policy", label: "Trade Compliance Policy", status: "Approved", doc: "TC-Policy-2024.pdf" },
            { id: "audits", label: "Internal Audit Records", status: "Approved", doc: "Audit-Log-Q4.pdf" },
            { id: "history", label: "Import/Export Evidence (2 Yrs)", status: "Approved", doc: "Customs-History.pdf" }
        ]
    },
    {
        id: "supplyChain",
        title: "Supply Chain Security",
        description: "Security measures throughout the logistics chain.",
        status: "Pending Review",
        progress: 75,
        tasks: [
            { id: "access", label: "Physical Access Controls", status: "Approved", doc: "Site-Security-Plan.pdf" },
            { id: "cyber", label: "Cyber Security Standards", status: "Pending Review", doc: "IT-Security-v2.pdf" },
            { id: "partners", label: "Service Provider Security", status: "Draft", doc: null }
        ]
    },
    {
        id: "financial",
        title: "Financial Viability",
        description: "Sound financial position for trade obligations.",
        status: "In Progress",
        progress: 40,
        tasks: [
            { id: "financials", label: "2 Years Audited Statements", status: "Needs Fix", doc: "Financials-2023.pdf", note: "Missing 2022 statement." },
            { id: "tax", label: "Tax Compliance Certificate", status: "Draft", doc: null },
            { id: "credit", label: "Creditworthiness Report", status: "Draft", doc: null }
        ]
    },
    {
        id: "personnel",
        title: "Personnel Security",
        description: "Vetting programs for logistics staff.",
        status: "Draft",
        progress: 10,
        tasks: [
            { id: "checks", label: "Background Check Program", status: "Draft", doc: null },
            { id: "training", label: "Security Training Logs", status: "Draft", doc: null }
        ]
    },
    {
        id: "finalReview",
        title: "Final Review",
        description: "Submission and validation by ABF.",
        status: "Locked",
        progress: 0,
        tasks: []
    }
];

const TIMELINE_DATA = [
    { event: "Application Created", date: "Feb 15, 2024", status: "done" },
    { event: "Trade Compliance Approved", date: "Feb 28, 2024", status: "done" },
    { event: "Cyber Security Document Re-uploaded", date: "Mar 3, 2024", status: "pending" },
    { event: "Financial Review Flagged", date: "Mar 4, 2024", status: "alert" }
];

import api from "../utils/api";

// Mock constant for the client ID (in a real app this would come from auth context)
const CLIENT_ID = "demo_client_123";

export default function ATTAccreditation() {
    const [activeStep, setActiveStep] = useState(null);
    const [viewMode, setViewMode] = useState("requirements");
    const [appData, setAppData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);

    // Dynamic steps based on backend data or defaults
    const [localSteps, setLocalSteps] = useState(STEPS);

    useEffect(() => {
        if (!CLIENT_ID) return;

        // 1. Initial fetch via API (to ensure app existence/get basic data)
        const initFetch = async () => {
            try {
                setLoading(true);
                const res = await api.get(`/api/att/status?clientId=${CLIENT_ID}`);
                setAppData(res.data);

                if (res.data.id) {
                    // 2. Setup Real-time listener once we have the ID
                    const unsubscribe = onSnapshot(doc(db, "attApplications", res.data.id), (docSnap) => {
                        if (docSnap.exists()) {
                            const data = docSnap.data();
                            setAppData({ id: docSnap.id, ...data });

                            // Merge backend step status with local STEPS definitions
                            if (data.steps) {
                                const merged = STEPS.map(s => ({
                                    ...s,
                                    ...(data.steps[s.id] || {}),
                                    tasks: s.tasks.map(t => ({
                                        ...t,
                                        ...(data.steps[s.id]?.tasks?.[t.id] || {})
                                    }))
                                }));
                                setLocalSteps(merged);
                            }
                        }
                    });
                    return unsubscribe;
                }
            } catch (err) {
                console.error("Failed to fetch ATT status:", err);
            } finally {
                setLoading(false);
            }
        };

        const cleanup = initFetch();
        return () => { if (cleanup && typeof cleanup === 'function') cleanup(); };
    }, []);

    const fetchStatus = async () => {
        // fetchStatus is now largely handled by onSnapshot for real-time updates,
        // but we'll keep it for explicit refreshes if needed.
        try {
            const res = await api.get(`/api/att/status?clientId=${CLIENT_ID}`);
            setAppData(res.data);
        } catch (err) {
            console.error("Manual refresh failed:", err);
        }
    };

    const handleUpload = async (stepId, taskId) => {
        try {
            setUploading(true);
            // Simulating a file upload result
            const mockFile = {
                name: `${taskId}_evidence_${String(Date.now()).slice(-4)}.pdf`,
                size: "1.2 MB",
                type: "application/pdf"
            };

            // 1. Save document metadata
            const docRes = await api.post("/api/att/upload", {
                applicationId: appData?.id || "new",
                clientId: CLIENT_ID,
                step: stepId,
                taskId: taskId,
                name: mockFile.name,
                url: `https://storage.tinyglobal.com/att/${mockFile.name}`
            });

            // Capture the real application ID (especially important if it was 'new')
            const resolvedAppId = docRes.data.applicationId;

            // 2. Update Application step status
            const currentSteps = appData?.steps || {};
            const updatedSteps = {
                ...currentSteps,
                [stepId]: {
                    ...currentSteps[stepId],
                    status: "pending_review",
                    tasks: {
                        ...currentSteps[stepId]?.tasks,
                        [taskId]: { uploaded: true, doc: mockFile.name, status: "pending_review" }
                    }
                }
            };

            // 3. Patch the application to update progress/steps
            if (resolvedAppId && resolvedAppId !== "undefined") {
                await api.patch(`/api/att/update/${resolvedAppId}`, {
                    steps: updatedSteps,
                    progress: calculateProgress(updatedSteps)
                });
            }

            await fetchStatus(); // Refresh everything
            alert(`Document ${mockFile.name} uploaded successfully!`);
        } catch (err) {
            console.error("Upload failed:", err);
            alert("Upload failed. Please check backend connectivity.");
        } finally {
            setUploading(false);
        }
    };

    const calculateProgress = (steps) => {
        // Simple logic: total tasks uploaded / total tasks possible
        let total = 0;
        let completed = 0;
        STEPS.forEach(s => {
            s.tasks.forEach(t => {
                total++;
                if (steps?.[s.id]?.tasks?.[t.id]?.uploaded) completed++;
            });
        });
        return Math.min(100, Math.round((completed / total) * 100));
    };

    const overallProgress = appData?.progress || 0;
    const readinessScore = Math.round(overallProgress * 0.8 + 20); // Simpler mock score

    const toggleStep = (id) => {
        setActiveStep(activeStep === id ? null : id);
    };

    const getStatusStyles = (status) => {
        const s = status?.toLowerCase();
        switch (s) {
            case "approved": return "bg-emerald-100 text-emerald-700 border-emerald-200";
            case "pending":
            case "pending_review":
            case "pending review": return "bg-blue-100 text-blue-700 border-blue-200";
            case "in progress": return "bg-gold-100 text-gold-700 border-gold-200";
            case "needs_fix":
            case "needs fix": return "bg-orange-100 text-orange-700 border-orange-200";
            case "rejected": return "bg-red-100 text-red-700 border-red-200";
            case "draft": return "bg-slate-100 text-slate-600 border-slate-200";
            case "locked": return "bg-slate-50 text-slate-300 border-slate-100 opacity-50";
            default: return "bg-slate-100 text-slate-600 border-slate-200";
        }
    };

    const getStatusLabel = (status) => {
        if (!status) return "Pending";
        const s = status.toLowerCase();
        if (s === "approved") return "Approved";
        if (s === "rejected") return "Rejected";
        if (s === "needs_fix" || s === "needs fix") return "Needs Fix";
        if (s === "pending_review" || s === "pending review") return "Pending Review";
        return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    };

    const renderProgressHeader = () => (
        <div className="bg-white rounded-2xl border border-slate-200 p-8 mb-8 shadow-sm overflow-hidden relative">
            <div className="absolute top-0 right-0 p-8 opacity-5">
                <ShieldCheck size={120} />
            </div>

            <div className="flex flex-col md:flex-row md:items-center justify-between gap-8 relative z-10">
                <div className="space-y-4 flex-1">
                    <div className="flex items-center gap-3">
                        <h2 className="text-2xl font-bold text-navy-900 font-serif">Accreditation Journey</h2>
                        <span className="px-3 py-1 bg-gold-500 text-navy-900 text-[10px] font-bold rounded-full uppercase tracking-tighter">Enterprise Mode</span>
                    </div>

                    <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                            <span className="font-bold text-slate-600">Overall Progress</span>
                            <span className="font-bold text-navy-900">{overallProgress}% Complete</span>
                        </div>
                        <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden border border-slate-200 p-0.5">
                            <div
                                className="h-full bg-gradient-to-r from-navy-900 to-navy-700 rounded-full transition-all duration-1000 ease-out"
                                style={{ width: `${overallProgress}%` }}
                            />
                        </div>
                    </div>
                </div>

                <div className="flex gap-4">
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 text-center min-w-[120px]">
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Readiness Score</p>
                        <p className="text-3xl font-bold text-navy-900">{readinessScore}%</p>
                        <div className="flex items-center justify-center gap-1 text-[10px] text-emerald-600 font-bold mt-1">
                            <TrendingUp size={10} /> +2% this week
                        </div>
                    </div>
                    <div className="bg-navy-900 p-4 rounded-xl border border-navy-800 text-center min-w-[120px] text-white">
                        <p className="text-[10px] font-bold text-white/50 uppercase tracking-widest mb-1">Status</p>
                        <p className="text-xl font-bold text-gold-500">Under Review</p>
                        <p className="text-[10px] text-white/30 mt-1 italic">ETA: 12 Days</p>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderTabs = () => (
        <div className="flex gap-2 p-1 bg-slate-100 rounded-xl mb-8 w-fit shadow-inner">
            {[
                { id: "requirements", label: "Journey Map", icon: ShieldCheck },
                { id: "documents", label: "Document Bucket", icon: FileText },
                { id: "application", label: "Review Panel", icon: MoreHorizontal },
                { id: "track", label: "Status Timeline", icon: History },
                { id: "chat", label: "Agent Expert", icon: MessageSquare }
            ].map(tab => (
                <button
                    key={tab.id}
                    onClick={() => setViewMode(tab.id)}
                    className={`px-4 py-2 rounded-lg text-xs font-bold flex items-center gap-2 transition-all ${viewMode === tab.id
                        ? "bg-white text-navy-900 shadow-md translate-y-[-1px]"
                        : "text-slate-500 hover:text-navy-700"
                        }`}
                >
                    <tab.icon size={14} /> {tab.label}
                </button>
            ))}
        </div>
    );

    const renderJourney = () => (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 relative">
            {uploading && (
                <div className="absolute inset-0 bg-white/50 z-50 flex items-center justify-center backdrop-blur-[1px]">
                    <div className="bg-navy-900 text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-4 animate-bounce">
                        <Upload className="animate-pulse text-gold-500" />
                        <span className="font-bold text-sm">Processing Evidence...</span>
                    </div>
                </div>
            )}

            {/* Steps Left Panel */}
            <div className="lg:col-span-2 space-y-4">
                {localSteps.map((step, idx) => (
                    <div
                        key={step.id}
                        className={`bg-white rounded-xl border transition-all duration-300 ${activeStep === step.id
                            ? "border-navy-900 shadow-lg ring-1 ring-navy-900/5"
                            : "border-slate-200 hover:border-slate-300 shadow-sm"
                            }`}
                    >
                        {/* Header */}
                        <div
                            className="p-5 flex items-center justify-between cursor-pointer"
                            onClick={() => toggleStep(step.id)}
                        >
                            <div className="flex items-center gap-4">
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm ${step.progress === 100 ? "bg-emerald-500 text-white" : "bg-slate-100 text-navy-900"
                                    }`}>
                                    {step.progress === 100 ? <CheckCircle2 size={16} /> : idx + 1}
                                </div>
                                <div>
                                    <h3 className="font-bold text-navy-900 text-sm leading-tight">{step.title}</h3>
                                    <p className="text-[11px] text-slate-500 mt-0.5">{step.description}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-4">
                                <span className={`text-[10px] font-bold px-2 py-1 rounded-md border ${getStatusStyles(step.status)}`}>
                                    {getStatusLabel(step.status)}
                                </span>
                                {activeStep === step.id ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
                            </div>
                        </div>

                        {/* Content */}
                        {activeStep === step.id && (
                            <div className="px-5 pb-5 pt-0 border-t border-slate-50 animate-in fade-in slide-in-from-top-2">
                                <div className="space-y-3 mt-4">
                                    <div className="flex justify-between items-center mb-2">
                                        <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Required Tasks</h4>
                                        <span className="text-[10px] text-gold-600 font-bold">{step.progress}% Complete</span>
                                    </div>
                                    {step.tasks.map(task => (
                                        <div key={task.id} className="group bg-slate-50 p-3 rounded-lg border border-slate-100 flex items-center justify-between hover:bg-white hover:border-navy-100 transition-all">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-2 h-2 rounded-full ${task.status?.toLowerCase() === "approved" ? "bg-emerald-500" :
                                                    task.status?.toLowerCase() === "needs_fix" || task.status?.toLowerCase() === "needs fix" ? "bg-orange-500" :
                                                        task.status?.toLowerCase() === "rejected" ? "bg-red-500" :
                                                            task.uploaded ? "bg-blue-500" : "bg-slate-300"
                                                    }`} />
                                                <div>
                                                    <p className="text-xs font-bold text-navy-900">{task.label}</p>
                                                    {task.doc && (
                                                        <p className="text-[10px] text-navy-400 underline flex items-center gap-1 cursor-pointer">
                                                            <FileText size={10} /> {task.doc}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                {task.note && (
                                                    <div className="group/note relative">
                                                        <AlertCircle size={14} className="text-red-500 cursor-help" />
                                                        <div className="hidden group-hover/note:block absolute bottom-full mb-2 right-0 w-48 p-2 bg-navy-900 text-white text-[10px] rounded shadow-xl z-20">
                                                            {task.note}
                                                        </div>
                                                    </div>
                                                )}
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleUpload(step.id, task.id); }}
                                                    disabled={uploading || task.status?.toLowerCase() === "approved"}
                                                    className={`text-[10px] font-bold px-3 py-1.5 rounded-md shadow-sm transition-all flex items-center gap-2 ${task.status?.toLowerCase() === "approved"
                                                        ? "bg-emerald-50 text-emerald-600 border border-emerald-100 cursor-not-allowed"
                                                        : "bg-white border border-slate-200 text-navy-900 hover:border-navy-900 shadow-sm active:scale-95"
                                                        }`}
                                                >
                                                    {task.status?.toLowerCase() === "approved" ? <CheckCircle2 size={12} /> : <Upload size={12} />}
                                                    {task.status?.toLowerCase() === "approved" ? "Verified" : (task.uploaded ? "Replace" : "Upload")}
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                    {step.tasks.length === 0 && (
                                        <div className="text-center py-6 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                                            <Clock size={20} className="mx-auto text-slate-300 mb-2" />
                                            <p className="text-xs text-slate-400">Section details will unlock after previous steps hit 90%.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Sidebar Right Panel */}
            <div className="space-y-6">
                {/* Readiness Card */}
                <div className="bg-navy-900 rounded-2xl p-6 text-white shadow-xl shadow-navy-900/20">
                    <div className="flex justify-between items-start mb-4">
                        <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                            <ShieldCheck className="text-gold-500" size={24} />
                        </div>
                        <Info size={16} className="text-white/30 cursor-pointer" />
                    </div>
                    <h3 className="text-lg font-bold mb-1">ATT Readiness Score</h3>
                    <p className="text-xs text-white/50 mb-6">Based on actual ABF verification criteria.</p>

                    <div className="space-y-4">
                        <div className="flex items-center justify-between text-xs">
                            <span className="text-white/70">Trade Compliance</span>
                            <span className="text-emerald-400 font-bold">Passed</span>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                            <span className="text-white/70">Security Measures</span>
                            <span className="text-gold-400 font-bold">Good</span>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                            <span className="text-white/70">Financial Stability</span>
                            <span className="text-red-400 font-bold">Low</span>
                        </div>
                    </div>

                    <button className="w-full mt-6 py-3 bg-gold-500 text-navy-900 font-bold text-xs rounded-xl hover:bg-gold-400 transition-all flex items-center justify-center gap-2">
                        Request Expert Audit <ExternalLink size={14} />
                    </button>
                </div>

                {/* Guidelines */}
                <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                    <h4 className="font-bold text-navy-900 text-sm mb-4">ABF Quick Resources</h4>
                    <div className="space-y-3">
                        <a href="#" className="flex items-center gap-3 p-3 rounded-lg border border-slate-50 hover:border-gold-500 group transition-all">
                            <div className="w-8 h-8 rounded-lg bg-gold-50 flex items-center justify-center text-gold-600 group-hover:bg-gold-500 group-hover:text-white">
                                <FileText size={16} />
                            </div>
                            <span className="text-[11px] font-bold text-slate-600">Accreditation Guide 2024</span>
                        </a>
                        <a href="#" className="flex items-center gap-3 p-3 rounded-lg border border-slate-50 hover:border-gold-500 group transition-all">
                            <div className="w-8 h-8 rounded-lg bg-gold-50 flex items-center justify-center text-gold-600 group-hover:bg-gold-500 group-hover:text-white">
                                <CheckCircle2 size={16} />
                            </div>
                            <span className="text-[11px] font-bold text-slate-600">Self-Assessment Tool</span>
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderTimeline = () => (
        <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm max-w-4xl mx-auto">
            <h3 className="text-xl font-bold text-navy-900 mb-1 font-serif">Status History</h3>
            <p className="text-xs text-slate-400 mb-10">Real-time update log for your accreditation process.</p>

            <div className="relative space-y-8 before:absolute before:left-3 before:top-2 before:bottom-2 before:w-0.5 before:bg-slate-100">
                {TIMELINE_DATA.map((item, idx) => (
                    <div key={idx} className="flex gap-6 relative group">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center z-10 border-4 border-white shadow-sm ring-1 ring-slate-100 ${item.status === "done" ? "bg-emerald-500" :
                            item.status === "pending" ? "bg-gold-500" : "bg-red-500"
                            }`}>
                            {item.status === "done" && <CheckCircle2 size={10} className="text-white" />}
                            {item.status === "pending" && <Clock size={10} className="text-white" />}
                            {item.status === "alert" && <AlertCircle size={10} className="text-white" />}
                        </div>
                        <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 group-hover:border-navy-100 transition-all flex-1">
                            <div className="flex justify-between items-start mb-1">
                                <h4 className="text-xs font-bold text-navy-900">{item.event}</h4>
                                <span className="text-[10px] text-slate-400 bg-white px-2 py-0.5 rounded-full border border-slate-100 font-medium">{item.date}</span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    return (
        <div className="pb-20 animate-in fade-in duration-700">
            {/* Page Header */}
            <div className="mb-10 flex flex-col md:flex-row md:items-end justify-between gap-6 border-b border-slate-200 pb-8">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-xl bg-navy-900 flex items-center justify-center text-gold-500 shadow-lg shadow-navy-900/10">
                            <ShieldCheck size={24} />
                        </div>
                        <h1 className="text-3xl lg:text-4xl font-bold text-navy-900 font-serif tracking-tight">ATT Accreditation</h1>
                    </div>
                    <p className="text-slate-500 text-lg max-w-2xl font-medium">Manage your Australian Trusted Trader compliance journey through our enterprise-grade advisory portal.</p>
                </div>
                <div className="flex gap-3">
                    <button className="px-5 py-2.5 bg-white border border-slate-200 text-navy-900 text-xs font-bold rounded-xl hover:bg-slate-50 transition-all flex items-center gap-2">
                        <Upload size={14} /> Export Packet
                    </button>
                    <button className="px-5 py-2.5 bg-navy-900 text-gold-500 text-xs font-bold rounded-xl hover:bg-navy-800 transition-all flex items-center gap-2 shadow-lg shadow-navy-900/10">
                        Agent Consultation
                    </button>
                </div>
            </div>

            {renderProgressHeader()}
            {renderTabs()}

            <main className="min-h-[600px]">
                {viewMode === "requirements" && renderJourney()}
                {viewMode === "track" && renderTimeline()}
                {viewMode === "chat" && (
                    <div className="h-[700px] border border-slate-200 rounded-3xl overflow-hidden shadow-2xl bg-white p-2">
                        <ChatBox role="Client" chatId="client-operator" />
                    </div>
                )}
                {viewMode === "documents" && (
                    <div className="space-y-6 max-w-5xl mx-auto">
                        <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm flex items-center justify-between">
                            <div>
                                <h2 className="text-xl font-bold text-navy-900">Enterprise Document Bucket</h2>
                                <p className="text-sm text-slate-500">Centralized repository for all compliance evidence.</p>
                            </div>
                            <button className="px-6 py-3 bg-navy-900 text-white rounded-xl font-bold text-xs hover:bg-navy-800 transition-all flex items-center gap-2">
                                <Upload size={16} className="text-gold-500" /> Bulk Upload Evidence
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {localSteps.filter(s => s.tasks.length > 0).map(step => (
                                <div key={step.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                                    <div className="p-4 bg-slate-50 border-b border-slate-100 flex justify-between items-center">
                                        <h4 className="font-bold text-navy-900 text-xs">{step.title}</h4>
                                        <span className="text-[10px] bg-white px-2 py-0.5 rounded border border-slate-200 font-bold text-slate-400">
                                            {step.tasks.filter(t => t.uploaded || t.doc).length}/{step.tasks.length} Uploaded
                                        </span>
                                    </div>
                                    <div className="divide-y divide-slate-100">
                                        {step.tasks.map(task => (
                                            <div key={task.id} className="p-4 flex items-center justify-between hover:bg-slate-50 transition-colors">
                                                <div className="flex items-center gap-3 text-left">
                                                    <FileText size={18} className={task.doc ? "text-navy-900" : "text-slate-200"} />
                                                    <div>
                                                        <p className="text-xs font-bold text-navy-900">{task.label}</p>
                                                        <p className={`text-[10px] font-medium ${(task.uploaded || task.doc) ? "text-navy-400" : "text-red-400"}`}>
                                                            {(task.uploaded || task.doc) ? (task.doc || "evidence.pdf") : "Action Required: Missing"}
                                                        </p>
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={() => handleUpload(step.id, task.id)}
                                                    disabled={uploading}
                                                    className={`text-[10px] font-bold px-3 py-1.5 rounded-lg border transition-all ${(task.uploaded || task.doc) ? "bg-white text-navy-900 border-slate-200 hover:border-navy-900" : "bg-navy-900 text-white border-navy-900"
                                                        }`}>
                                                    {(task.uploaded || task.doc) ? "Manage" : "Upload"}
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {viewMode === "application" && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        <div className="lg:col-span-2 space-y-6">
                            <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
                                <div className="flex justify-between items-center mb-6">
                                    <h3 className="text-xl font-bold text-navy-900 font-serif">Submission Audit Panel</h3>
                                    <div className={`px-4 py-1.5 rounded-full text-xs font-bold border ${readinessScore >= 95 ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-red-100 text-red-700 border-red-200"}`}>
                                        {readinessScore >= 95 ? "Accreditation Ready" : "Incomplete Readiness"}
                                    </div>
                                </div>
                                <p className="text-sm text-slate-500 mb-8 leading-relaxed">
                                    This panel provides a filtered view of all critical compliance markers. Our internal experts use this dashboard to signal the Australian Border Force when your application is watertight.
                                </p>

                                <div className="space-y-4">
                                    <div className="p-4 rounded-xl border border-slate-100 bg-slate-50 flex items-center justify-between opacity-50">
                                        <div className="flex items-center gap-3">
                                            <ShieldCheck size={20} className="text-slate-400" />
                                            <span className="text-sm font-bold text-slate-500">ABF API Handshake</span>
                                        </div>
                                        <span className="text-[10px] font-bold text-slate-400 italic">Unlocks at 95%</span>
                                    </div>
                                    <div className="p-4 rounded-xl border border-slate-100 bg-slate-50 flex items-center justify-between opacity-50">
                                        <div className="flex items-center gap-3">
                                            <FileText size={20} className="text-slate-400" />
                                            <span className="text-sm font-bold text-slate-500">Boleto / Fee Settlement</span>
                                        </div>
                                        <span className="text-[10px] font-bold text-slate-400 italic">Unlocks at 95%</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-6">
                            <div className="bg-gold-50 rounded-2xl p-6 border border-gold-200 border-dashed">
                                <h4 className="text-sm font-bold text-gold-800 mb-4 flex items-center gap-2">
                                    <AlertCircle size={16} /> Expert Agent Panel
                                </h4>
                                <div className="space-y-4">
                                    <div className="bg-white p-4 rounded-xl border border-gold-100 shadow-sm">
                                        <p className="text-[10px] font-bold text-gold-600 uppercase tracking-widest mb-2">Internal Note (Mar 3)</p>
                                        <p className="text-xs text-navy-900 leading-relaxed italic">
                                            "Cyber security document IT-Security-v2 looks good. Need to clarify Site Security Plan access levels for external contractors before approval."
                                        </p>
                                        <div className="mt-4 pt-3 border-t border-slate-50 flex items-center gap-2">
                                            <div className="w-6 h-6 rounded-full bg-navy-900 text-white flex items-center justify-center text-[10px] font-bold">LM</div>
                                            <span className="text-[10px] font-bold text-navy-900">Lucas (Senior Auditor)</span>
                                        </div>
                                    </div>

                                    <button className="w-full py-3 bg-navy-900 text-white rounded-xl font-bold text-xs hover:bg-navy-800 transition-all">
                                        Acknowledge Agent Feedback
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}
